import React, { ComponentType } from "react"
import { NodeProps } from "reactflow"
import { Grid, Tooltip } from "@material-ui/core"
import { BlockNodeData } from "@/protocols/chatBotFlow"

import useChatBotFlowBlockDetails from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowBlockDetails"

import BaseBlock from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BaseBlock"
import BlockBodySection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySection"
import BlockBodySectionContainer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionContainer"
import BlockBodySectionGroup from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionGroup"
import BlockBodySectionHandle from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionHandle"
import CreateConditionButton from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/CreateConditionButton"
import EditTimeoutConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/EditTimeoutConditionSection"
import InvalidResponseConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ElseConditionSection"
import MessageSendFailedConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/MessageSendFailedConditionSection"

import MenuListMessageDialog from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/MenuListBlock/MenuListMessageDialog"
import MenuListItemEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/MenuListBlock/MenuListItemEditor"

import { getContinueConditionRuleFormattedForCreation, getMessageButtonText } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockRule"
import { generateUUID } from "@/utils/id"

const MAX_OPTIONS_COUNT = 10

const MenuListBlock: ComponentType<NodeProps<BlockNodeData>> = (node) => {
	const chatBotFlowBlockDetails = useChatBotFlowBlockDetails({ chatBotFlowBlockId: node.data.chatBotFlowBlockId })

	const conditionRules = chatBotFlowBlockDetails.nextChatBotFlowBlockRules.filter(({ category }) => category === "continue-condition")

	const reachedMaxOptionsCount = conditionRules.length >= MAX_OPTIONS_COUNT

	const menuListData = chatBotFlowBlockDetails.content?.menuListData || {}

	const rowIdToRowDescription = menuListData.rowIdToRowDescription || {}

	return (
		<BaseBlock
			node={node}
		>
			<MenuListMessageDialog
				chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
				onSave={(content) => chatBotFlowBlockDetails.changeContent(content)}
				chatBotFlowBlockId={node.data.chatBotFlowBlockId}
			/>

			<BlockBodySectionGroup>
				{conditionRules?.map(rule => (
					<BlockBodySection
						key={rule.id}
					>
						<BlockBodySectionHandle
							chatBotFlowBlockRule={rule}
						>
							<MenuListItemEditor
								onSave={(chatBotFlowBlockRule) => chatBotFlowBlockDetails.changeRule("UPDATE", chatBotFlowBlockRule)}
								onDelete={() => chatBotFlowBlockDetails.changeRule("DELETE", rule)}
								onChangeDescription={(description) => {
									chatBotFlowBlockDetails.changeContent({
										menuListData: {
											...menuListData,
											rowIdToRowDescription: {
												...rowIdToRowDescription,
												[rule.id]: description
											}
										}
									})
								}}
								description={rowIdToRowDescription[rule.id] || ""}
								chatBotFlowBlockRule={rule}
							>
								<BlockBodySectionContainer
									styles={{ width: 220, minHeight: 40, padding: "8px 16px" }}
									text={getMessageButtonText(rule)}
								/>
							</MenuListItemEditor>
						</BlockBodySectionHandle>
					</BlockBodySection>
				))}
			</BlockBodySectionGroup>

			<BlockBodySection>
				<Tooltip
					title=""
					{...(reachedMaxOptionsCount && ({
						title: "Não é possível utilizar mais de 10 opções devido à limitação do Whatsapp",
						style: {
							cursor: "not-allowed"
						}
					}))}
				>
					<Grid>
						<MenuListItemEditor
							hideChildSelection
							disabled={reachedMaxOptionsCount}
							onSave={(chatBotFlowBlockRule) => {
								const newRuleId = generateUUID()

								chatBotFlowBlockRule.id = newRuleId

								chatBotFlowBlockDetails.changeRule("CREATE", {
									...chatBotFlowBlockRule,
									next_chat_bot_flow_block_id: null,
									validations: chatBotFlowBlockRule?.validations || []
								})
							}}
							onChangeDescription={(description, savedRule) => {
								chatBotFlowBlockDetails.changeContent({
									menuListData: {
										...menuListData,
										rowIdToRowDescription: {
											...rowIdToRowDescription,
											[savedRule.id]: description
										}
									}
								})
							}}
							description=""
							chatBotFlowBlockRule={
								getContinueConditionRuleFormattedForCreation(
									chatBotFlowBlockDetails.blockConstructionResource?.available_next_block_flow_rules
								)
							}
						>
							<CreateConditionButton
								disabled={reachedMaxOptionsCount}
								color={chatBotFlowBlockDetails.blockThemeConfig.color.blockIcon}
							>
								Adicionar opção
							</CreateConditionButton>
						</MenuListItemEditor>
					</Grid>
				</Tooltip>
			</BlockBodySection>

			<BlockBodySectionGroup>
				<InvalidResponseConditionSection
					nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
					text="Se nenhuma resposta for válida"
				/>

				<EditTimeoutConditionSection
					nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
					onSave={(chatBotFlowBlockRule) => chatBotFlowBlockDetails.changeRule("UPDATE", chatBotFlowBlockRule)}
					renderText={(timeText) => <span>{timeText} sem interação</span>}
					chatBotFlowChannelType={node.data.chatBotFlowChannelType}
				/>
			</BlockBodySectionGroup>

			<MessageSendFailedConditionSection
				nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
				chatBotFlowBlockId={node.data.chatBotFlowBlockId}
				chatBotFlowChannelType={node.data.chatBotFlowChannelType}
			/>
		</BaseBlock>
	)
}

export default MenuListBlock
